import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import DateTimeDisplay from './DateTimeDisplay';
import TimetableNotificationCard from './TimetableNotificationCard';
import StudentSecurityCard from './StudentSecurityCard';
import StudentAssignments from './StudentAssignments';
import Tooltip from './Tooltip';
import CallCenter from './CallCenter';
import './CallCenter.css';
import DashboardMail from './DashboardMail';

const StudentDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const storedStudent = JSON.parse(localStorage.getItem('studentUser') || 'null');
  const student = location.state?.student || storedStudent;
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);
  const [pendingAssignments, setPendingAssignments] = useState(0);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [activeComponent, setActiveComponent] = useState(null);
  const [feeSummary, setFeeSummary] = useState(null);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (student) {
      loadUnreadCount();
      loadPendingAssignmentsCount();
      fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/student/${student.username}`)
        .then(response => response.ok ? response.json() : null)
        .then(data => setFeeSummary(data?.summary || null))
        .catch(() => setFeeSummary(null));
    }
  }, [student]);

  const loadUnreadCount = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/announcements/${student.username}`, { credentials: 'include' });
      const announcements = await response.json();
      const unread = announcements.filter(announcement => !announcement.isRead).length;
      setUnreadAnnouncements(unread);
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const loadPendingAssignmentsCount = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/student/${student.username}`);
      const assignments = await response.json();
      const pending = assignments.filter(assignment => !assignment.submitted).length;
      setPendingAssignments(pending);
    } catch (error) {
      console.error('Error loading pending assignments:', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const cards = [
    {
      title: 'Learn',
      icon: 'L',
      color: '#0ea5e9',
      softColor: '#e0f2fe',
      accentColor: '#0284c7',
      description: 'Access your lessons and study materials.',
      onClick: () => navigate('/student/learn', { state: { student } }),
      tooltip: 'Access all your class lessons, study materials, and learning resources. Browse subjects, watch video lessons, read notes, and complete interactive exercises. Track your learning progress and review completed topics.'
    },
    {
      title: 'Midterm Exam',
      icon: 'M',
      color: '#8b5cf6',
      softColor: '#ede9fe',
      accentColor: '#7c3aed',
      description: 'Take your midterm examinations.',
      onClick: () => navigate('/student/midterm', { state: { student } }),
      tooltip: 'Take midterm examinations for your subjects. Ensure you have a stable internet connection and quiet environment. Exams are timed, so manage your time wisely. Results will be available after submission and grading.'
    },
    {
      title: 'Term Final Exam',
      icon: 'F',
      color: '#ef4444',
      softColor: '#fee2e2',
      accentColor: '#dc2626',
      description: 'Attempt your term final exams.',
      onClick: () => navigate('/student/final', { state: { student } }),
      tooltip: 'Complete your final term examinations. These comprehensive exams cover all topics from the term. Make sure to review all subjects before attempting. Your final grades depend on these exam results.'
    },
    {
      title: 'Complain / Share Idea',
      icon: 'I',
      color: '#f97316',
      softColor: '#ffedd5',
      accentColor: '#ea580c',
      description: 'Send feedback or share your ideas.',
      onClick: () => navigate('/student/complain', { state: { student } }),
      tooltip: 'Share your feedback, report issues, or suggest improvements to the school. Your input helps us make the learning experience better. Be specific about your concerns or ideas for faster resolution.'
    },
    {
      title: 'Progress Board',
      icon: 'P',
      color: '#22c55e',
      softColor: '#dcfce7',
      accentColor: '#16a34a',
      description: 'Check your progress so far.',
      onClick: () => navigate('/student/board', { state: { student } }),
      tooltip: 'View your academic progress across all subjects. See your grades, attendance records, completed assignments, and performance trends. Identify areas where you need to improve and celebrate your achievements.'
    },
    {
      title: 'School News Board',
      icon: 'N',
      color: '#06b6d4',
      softColor: '#cffafe',
      accentColor: '#0891b2',
      description: 'Stay updated with school news and announcements.',
      onClick: () => navigate('/student/news', { state: { student } }),
      tooltip: 'Read important school announcements, news, and updates. Stay informed about school events, schedule changes, holiday notices, and important deadlines. Mark announcements as read after reviewing them.'
    },
    {
      title: 'Parent Pay Center',
      icon: '₦',
      color: '#10b981',
      softColor: '#d1fae5',
      accentColor: '#059669',
      description: feeSummary
        ? Number(feeSummary.totalBalance || 0) > 0
          ? `Outstanding balance: NGN ${Number(feeSummary.totalBalance).toLocaleString()}.`
          : `Paid in full. NGN ${Number(feeSummary.totalPaid || 0).toLocaleString()} received.`
        : 'Loading your school fee status...',
      onClick: () => navigate('/parent-login'),
      tooltip: 'School fee payments now belong to parent accounts. Ask your parent or guardian to sign in to review balances and complete payment.'
    },
    {
      title: 'Student Profile',
      icon: 'S',
      color: '#ec4899',
      softColor: '#fce7f3',
      accentColor: '#db2777',
      description: 'Manage your profile and view achievements.',
      onClick: () => navigate('/student/profile', { state: { student } }),
      tooltip: 'Update your profile information, change your profile picture, and view your achievements and certificates. Keep your contact information current so teachers and parents can reach you.'
    },
    {
      title: 'Class Tracker',
      icon: 'C',
      color: '#14b8a6',
      softColor: '#ccfbf1',
      accentColor: '#0d9488',
      description: 'Check attendance and class schedules.',
      onClick: () => navigate('/student/class-tracker', { state: { student } }),
      tooltip: 'Monitor your attendance record and view your class schedule. See which classes you have each day, track your attendance percentage, and identify any attendance concerns that need to be addressed.'
    },
    {
      title: 'Learning Hub',
      icon: 'H',
      color: '#a855f7',
      softColor: '#f3e8ff',
      accentColor: '#9333ea',
      description: 'Advanced learning tools and analytics.',
      onClick: () => navigate('/student/learning-hub', { state: { student } }),
      tooltip: 'Access advanced learning tools, view detailed analytics about your performance, and get personalized study recommendations. Use interactive charts to understand your learning patterns and improve your study habits.'
    },
    {
      title: 'Kidstoon',
      icon: 'K',
      color: '#f59e0b',
      softColor: '#fef3c7',
      accentColor: '#d97706',
      description: 'Watch safe educational videos approved by teachers.',
      onClick: () => navigate('/student/kidstoon', { state: { student } }),
      tooltip: 'Watch educational videos that are carefully selected and approved by your teachers. Videos are age-appropriate and cover various subjects to make learning more engaging and fun.'
    },
    {
      title: 'Games',
      icon: 'G',
      color: '#84cc16',
      softColor: '#ecfccb',
      accentColor: '#65a30d',
      description: 'Play fun educational games tailored to your class.',
      onClick: () => navigate('/student/games', { state: { student } }),
      tooltip: 'Play educational games designed for your grade level. Games reinforce learning concepts while being fun and interactive. Track your game scores and unlock achievements as you learn.'
    },
    {
      title: 'Assignments',
      icon: 'A',
      color: '#3b82f6',
      softColor: '#dbeafe',
      accentColor: '#2563eb',
      description: 'Complete assignments assigned by your teachers.',
      component: 'assignments',
      tooltip: 'View and complete assignments from your teachers. Submit your work on time, track submission status, and view feedback from teachers. Late submissions may affect your grades.'
    },
    // Disabled features (Coming Soon)
    {
      title: 'Mentorship Program',
      icon: 'MP',
      color: '#94a3b8',
      softColor: '#f1f5f9',
      accentColor: '#64748b',
      description: 'Coming Soon: Connect with senior students and teachers.',
      onClick: () => alert('Mentorship Program - Coming Soon!'),
      disabled: true,
      tooltip: 'This feature is currently under development. Soon you will be able to connect with senior students and teachers for guidance and mentorship in your academic journey.'
    },
    {
      title: 'Study Groups',
      icon: 'SG',
      color: '#94a3b8',
      softColor: '#f1f5f9',
      accentColor: '#64748b',
      description: 'Coming Soon: Join peer learning communities.',
      onClick: () => alert('Study Groups - Coming Soon!'),
      disabled: true,
      tooltip: 'This feature is coming soon. You will be able to join study groups with classmates to collaborate on assignments, share knowledge, and learn together in a supportive environment.'
    },
    {
      title: 'Career Guidance',
      icon: 'CG',
      color: '#94a3b8',
      softColor: '#f1f5f9',
      accentColor: '#64748b',
      description: 'Coming Soon: Resources for future planning.',
      onClick: () => alert('Career Guidance - Coming Soon!'),
      disabled: true,
      tooltip: 'Career guidance resources are being prepared. Soon you will have access to career exploration tools, future planning resources, and guidance for choosing your academic path.'
    },
    {
      title: 'Extracurricular Activities',
      icon: 'EA',
      color: '#94a3b8',
      softColor: '#f1f5f9',
      accentColor: '#64748b',
      description: 'Coming Soon: Track club memberships and events.',
      onClick: () => alert('Extracurricular Activities - Coming Soon!'),
      disabled: true,
      tooltip: 'This feature is under development. Soon you will be able to track your participation in clubs, sports teams, and other extracurricular activities, plus view upcoming events and competitions.'
    },
  ];

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = async () => {
    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: student.username,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const result = await response.json();

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Failed to change password. Please try again.');
    }
  };

  const handleLogout = () => {
    // Clear student session data and redirect to login
    navigate('/student-login');
  };

  if (!student) {
    navigate('/student-login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#fff7ed] p-4 md:p-8 student-color-page">
      <div className="max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-5 mb-8 bg-white rounded-[2rem] p-5 md:p-7 shadow-xl border-4 border-sky-200">
        <div className="flex items-center space-x-4">
          {/* Profile Picture */}
          <div className="w-20 h-20 rounded-[1.6rem] overflow-hidden border-4 border-amber-300 shadow-lg bg-amber-100">
            <img
              src={student.profilePicture || '/fallback-data/default-avatar.png'}
              alt={`${student.firstName} ${student.lastName}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/fallback-data/default-avatar.png';
              }}
            />
          </div>
          <div>
            <p className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-black mb-2">
              Creative Town Student Space
            </p>
            <h1 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Welcome, {student.firstName} {student.lastName}!
            </h1>
            <p className="text-base text-slate-600 mt-2 font-semibold">Student ID: {student.username} - {student.className}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={() => setShowPasswordChange(true)}
            className="bg-sky-600 text-white px-5 py-3 rounded-2xl hover:bg-sky-700 transition duration-300 font-bold shadow-lg"
          >
            Change Password
          </button>
          <DateTimeDisplay />
          <button
            onClick={handleLogout}
            className="bg-rose-600 text-white px-5 py-3 rounded-2xl hover:bg-rose-700 transition duration-300 font-bold shadow-lg"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Timetable Notification Card */}
      <div className="mb-8">
        <TimetableNotificationCard userType="student" studentClass={student.className} />
      </div>

      {/* Security Card */}
      <div className="mb-8">
        <StudentSecurityCard student={student} />
      </div>

      <DashboardMail userType="student" user={student} />

      {/* Active Component Display */}
      {activeComponent === 'assignments' && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-blue-900">Assignments</h2>
            <button
              onClick={() => setActiveComponent(null)}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Back to Dashboard
            </button>
          </div>
          <StudentAssignments studentId={student.username} />
        </div>
      )}

      {/* Dashboard Cards */}
      {!activeComponent && (
        <>
        <div className="mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="text-sm font-black uppercase tracking-wide text-fuchsia-700">Choose your adventure</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">Your colorful learning cards</h2>
          </div>
          <div className="bg-white border-2 border-amber-200 rounded-2xl px-4 py-3 shadow text-sm font-bold text-slate-700">
            Learn, play, connect, and grow
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <div className="md:col-span-2 xl:col-span-2">
            <CallCenter
              identity={{
                type: 'student',
                id: student.username,
                name: `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.username
              }}
            />
          </div>
          {cards.map((card, index) => (
            <div
              key={index}
              style={{
                background: card.disabled
                  ? '#f1f5f9'
                  : `linear-gradient(180deg, ${card.softColor} 0%, #ffffff 62%)`,
                borderColor: card.disabled ? '#cbd5e1' : card.color,
                boxShadow: card.disabled
                  ? '0 12px 26px rgba(100, 116, 139, 0.12)'
                  : `0 18px 35px ${card.color}24`
              }}
              onClick={
                card.disabled
                  ? undefined
                  : card.component === 'assignments'
                  ? () => setActiveComponent('assignments')
                  : card.onClick
              }
              className={`rounded-[1.7rem] p-5 transition duration-300 border-4 relative overflow-hidden min-h-[210px] flex flex-col justify-between ${
                card.disabled
                  ? 'cursor-not-allowed opacity-70'
                  : 'hover:-translate-y-1 hover:scale-[1.01] cursor-pointer'
              }`}
            >
            {/* Unread Announcements Badge */}
            {card.title === 'School News Board' && unreadAnnouncements > 0 && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                {unreadAnnouncements > 99 ? '99+' : unreadAnnouncements}
              </div>
            )}

            {/* Pending Assignments Badge */}
            {card.title === 'Assignments' && pendingAssignments > 0 && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                {pendingAssignments > 99 ? '99+' : pendingAssignments}
              </div>
            )}

            <div>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg"
                style={{ backgroundColor: card.color }}
              >
                {card.icon}
              </div>
              {!card.disabled && (
                <Tooltip content={card.tooltip} position="top">
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black cursor-help bg-white shadow"
                    style={{ color: card.accentColor }}
                  >
                    ?
                  </span>
                </Tooltip>
              )}
            </div>
            <div className="mb-3">
              <h2 className={`text-2xl font-black leading-tight ${
                card.disabled ? 'text-gray-500' : 'text-slate-900'
              }`}>
                {card.title}
              </h2>
              {card.disabled && <span className="inline-block text-xs text-orange-600 mt-2 font-black bg-orange-100 px-2 py-1 rounded-full">Coming Soon</span>}
            </div>
            <p className={`text-sm leading-relaxed ${card.disabled ? 'text-gray-500' : 'text-slate-700'}`}>
              {card.description}
              {card.title === 'School News Board' && !loadingAnnouncements && (
                <span className="block text-xs mt-2 text-blue-700 font-black">
                  {unreadAnnouncements > 0 ? `${unreadAnnouncements} unread` : 'All caught up!'}
                </span>
              )}
            </p>
            </div>

            {/* News Priority Indicator */}
            {card.title === 'School News Board' && unreadAnnouncements > 0 && (
              <div className="mt-4 text-xs text-orange-700 bg-orange-100 rounded-full px-3 py-2 font-black">
                New announcements available
              </div>
            )}
            <div className="mt-5 h-2 rounded-full" style={{ backgroundColor: card.color }} />
          </div>
          ))}
        </div>
        </>
      )}

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Change Password</h3>

              {passwordError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  {passwordSuccess}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handlePasswordChange}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default StudentDashboard;
